import { Flex } from '@mantine/core';
import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { GeoSetTargetable } from '@feathr/blackbox';
import { Button, NumberCell, Pill } from '@feathr/components';
import { cssVar } from '@feathr/hooks';

import GeoFiltersOptions from '../GeoFiltersOptions';

function geoFiltersColumns(
  t: TFunction,
  onEdit: (targetable: GeoSetTargetable) => void,
  onSetDefault: (targetable: GeoSetTargetable) => void,
  defaultGeoSetId?: string,
  campaignCountsByGeoSetId?: Map<string, number>,
): Array<ColumnDef<GeoSetTargetable>> {
  return [
    {
      id: 'name',
      // accessorFn is what makes the column sortable — TanStack's getCanSort() is false
      // for a display (accessor-less) column even with global sorting on. The custom cell
      // still renders the link + Default pill.
      accessorFn: (row): string => row.get('name') ?? '',
      header: t('Geofilter set'),
      // Takes the width the fixed Campaigns (150) / Options (100) columns leave, so the table
      // fills its container. Replaces an oversized `size` that only filled under table-layout:auto.
      meta: { isFlex: true },
      cell({ row }): JSX.Element {
        const targetable = row.original;

        function handleEdit(): void {
          onEdit(targetable);
        }

        return (
          <Observer>
            {(): JSX.Element => (
              <Flex align={'center'} gap={cssVar('--spacing-2')}>
                <Button onClick={handleEdit} type={'link'}>
                  {targetable.get('name')}
                </Button>
                {targetable.isDefault(defaultGeoSetId) && (
                  <Pill theme={'neutral'}>{t('Default')}</Pill>
                )}
              </Flex>
            )}
          </Observer>
        );
      },
    },
    {
      id: 'campaigns',
      header: t('Campaigns'),
      enableSorting: false,
      meta: { align: 'right' },
      size: 150,
      cell({ row }): JSX.Element {
        return (
          <NumberCell
            format={'count'}
            value={campaignCountsByGeoSetId?.get(row.original.id) ?? 0}
          />
        );
      },
    },
    {
      // Hand-built rather than createActionMenuColumn: the cell must host GeoFiltersOptions,
      // which owns the stateful delete ArchiveModal rendered outside the menu.
      id: 'options',
      header: t('Options'),
      enableSorting: false,
      meta: { align: 'center' },
      size: 100,
      cell({ row }): JSX.Element {
        // GeoFiltersOptions is already observer-wrapped, so no Observer here.
        return (
          <GeoFiltersOptions
            onEdit={onEdit}
            onSetDefault={onSetDefault}
            targetable={row.original}
          />
        );
      },
    },
  ];
}

export default geoFiltersColumns;
